import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../../../components/Config/api.service';
import { FaEdit, FaTrash, FaPlus, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const AbsenceType = ({ setIsDrawerOpen, refreshParent, isInForm }) => {
    const [absenceTypes, setAbsenceTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentType, setCurrentType] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total_pages: 1,
        total_count: 0
    });
    const [formData, setFormData] = useState({
        name: '',
        category: 'paid',
        description: ''
    });

    // Category display mapping - improves maintainability
    const categoryLabels = useMemo(() => ({
        paid: 'Payés',
        unpaid: 'non Payés',
        deducted: 'déduits des congés',
    }), []);

    const fetchAbsenceTypes = useCallback(async (page = 1, search = '') => {
        setIsLoading(true);
        setError(null); // Reset error state before new request
        
        try {
            // Construct query params
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', pagination.limit);
            if (search) {
                params.append('search', search);
            }
            
            const response = await apiService.getElement(`/absence_types?${params.toString()}`);
            if (response.data && response.data.data) {
                setAbsenceTypes(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            } else {
                setError('Invalid response format');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
            setError(`Error fetching absence types: ${errorMessage}`);
            console.error('Error fetching absence types:', err);
        } finally {
            setIsLoading(false);
        }
    }, [pagination.limit]);

    useEffect(() => {
        fetchAbsenceTypes(pagination.page, searchTerm);
    }, [fetchAbsenceTypes]);

    const handleEditClick = useCallback((type) => {
        setCurrentType(type);
        setFormData({
            name: type.name,
            category: type.category,
            description: type.description || ''
        });
        setIsEditing(true);
    }, []);

    const handleNewClick = useCallback(() => {
        setCurrentType(null);
        setFormData({
            name: '',
            category: 'paid',
            description: ''
        });
        setIsEditing(true);
    }, []);

    const handleDeleteClick = useCallback(async (id) => {
        if (!window.confirm('Are you sure you want to delete this absence type?')) {
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            await apiService.updateElementById(`/absence_types`, id, { is_deleted: true });
            await fetchAbsenceTypes(pagination.page, searchTerm);
            if (refreshParent) refreshParent(); 
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
            setError(`Error deleting absence type: ${errorMessage}`);
            console.error('Error deleting absence type:', err);
        } finally {
            setIsLoading(false);
        }
    }, [fetchAbsenceTypes, refreshParent, pagination.page, searchTerm]);

    const handlePageChange = useCallback((page) => {
        fetchAbsenceTypes(page, searchTerm);
    }, [fetchAbsenceTypes, searchTerm]);

    const handleSearchChange = useCallback((e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        fetchAbsenceTypes(1, newSearchTerm);
    }, [fetchAbsenceTypes]);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault(); // Prevent form submission from refreshing the page
        
        // Basic validation
        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            if (currentType) {
                await apiService.updateElementById('/absence_types', currentType.id, formData);
            } else {
                await apiService.saveElement('/absence_types', formData);
            }
            
            setIsEditing(false);
            await fetchAbsenceTypes(pagination.page, searchTerm);
            if (refreshParent) refreshParent();
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
            setError(`Error saving absence type: ${errorMessage}`);
            console.error('Error saving absence type:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setError(null);
    }, []);

    // Render form component using a div instead of form when inside another form
    const renderForm = () => {
        const formContent = (
            <>
                <h2 className="text-2xl font-semibold mb-6">
                    {currentType ? 'Edit Absence Type' : 'Add New Absence Type'}
                </h2>
                
                {error && <div className="alert alert-error mb-4">{error}</div>}
                
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <input
                        type="text"
                        name="name"
                        className="input input-bordered w-full"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                    />
                </div>
                
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                        name="category"
                        className="select select-bordered w-full"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                    >
                        {Object.entries(categoryLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
                
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                        name="description"
                        className="textarea textarea-bordered w-full"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="3"
                        disabled={isLoading}
                    ></textarea>
                </div>
                
                <div className="flex justify-end gap-4 mt-6">
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        type={isInForm ? "button" : "submit"}
                        className="btn btn-outline bg-[--color-primary] text-white"
                        disabled={isLoading}
                        onClick={isInForm ? handleSubmit : undefined}
                    >
                        {isLoading ? 'Processing...' : currentType ? 'Update' : 'Create'}
                    </button>
                </div>
            </>
        );
        
        // If we're inside another form, use a div instead of a form tag
        if (isInForm) {
            return (
                <div className="p-6 max-w-3xl mx-auto bg-white rounded-lg shadow-md">
                    {formContent}
                </div>
            );
        }
        
        // Otherwise use a normal form tag
        return (
            <div className=" max-w-3xl mx-auto rounded-lg">
                <form onSubmit={handleSubmit}>
                    {formContent}
                </form>
            </div>
        );
    };

    // Render main list component
    const renderList = () => (
        <div className="pt-4 max-w-3xl mx-auto  rounded-lg">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Absence Types</h2>
                <button
                    className="btn rounded-full btn-outline bg-[--color-primary] text-white"
                    onClick={handleNewClick}
                    disabled={isLoading}
                >
                    <FaPlus className="mr-1" /> Add New Type
                </button>
            </div>
            
            {error && <div className="alert alert-error mb-4">{error}</div>}
            
            {/* Search box for absence types */}
            {/* <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search absence types..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="input input-bordered input-sm w-full"
                    disabled={isLoading}
                />
            </div> */}
            
            {isLoading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="loader">...</div>
                </div>
            ) : (
                <>
                    {absenceTypes.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No absence types found. Create your first one!
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table w-full">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Category</th>
                                        <th>Description</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {absenceTypes.map(type => (
                                        <tr key={type.id}>
                                            <td className="font-medium">{type.name}</td>
                                            <td>{categoryLabels[type.category] || type.category}</td>
                                            <td className="max-w-xs truncate">{type.description || '-'}</td>
                                            <td>
                                                <div className="flex gap-2">
                                                    <button
                                                        className="btn btn-xs btn-outline"
                                                        onClick={() => handleEditClick(type)}
                                                        aria-label="Edit"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        className="btn btn-xs btn-outline btn-error"
                                                        onClick={() => handleDeleteClick(type.id)}
                                                        aria-label="Delete"
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {/* Pagination controls */}
                    {pagination.total_pages > 1 && (
                        <div className="mt-4 p-4 flex">
                            <div className="flex justify-center md:justify-between w-full">
                                <button
                                    className={`hidden md:flex btn btn-sm text-[--color-primary] hover:bg-[--color-primary] items-center px-4 rounded-md ${pagination.page === 1 ? 'cursor-not-allowed' : 'btn-outline'}`}
                                    onClick={() => pagination.page > 1 && handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1 || isLoading}
                                >
                                    <FaArrowLeft /> Précédent
                                </button>

                                <div className="flex gap-2">
                                    {Array.from({ length: pagination.total_pages }, (_, index) => (
                                        <button
                                            key={index + 1}
                                            className={`btn btn-sm rounded-md ${pagination.page === index + 1 ? 'bg-[--color-primary] text-white' : ''}`}
                                            onClick={() => handlePageChange(index + 1)}
                                            disabled={isLoading}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className={`hidden md:flex btn btn-sm text-[--color-primary] hover:bg-[--color-primary] items-center px-4 rounded-md ${pagination.page === pagination.total_pages ? 'cursor-not-allowed' : 'btn-outline'}`}
                                    onClick={() => pagination.page < pagination.total_pages && handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.total_pages || isLoading}
                                >
                                    Suivant <FaArrowRight />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
            
            <div className="flex justify-end mt-6">
                {setIsDrawerOpen && (
                    <button
                        className="btn btn-outline"
                        onClick={() => setIsDrawerOpen(false)}
                        disabled={isLoading}
                    >
                        Close
                    </button>
                )}
            </div>
        </div>
    );

    return isEditing ? renderForm() : renderList();
};

export default AbsenceType;